import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { BookmarkX } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useReads } from "@/contexts/ReadsContext";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ReadBadge, PathwayProgress } from "@/components/ReadIndicators";
import PageSections from "@/components/PageSections";
import { defaultBookmarksIntroSections } from "@/components/sectionTypes";

export default function Bookmarks() {
  const { user, loading } = useAuth();
  const { isRead } = useReads();
  const [data, setData] = useState({ blogs: [], pathways: [] });
  const [pending, setPending] = useState(null); // {target_type, target_id, title}
  const [unreadOnly, setUnreadOnly] = useState(false);

  const refresh = () => api.get("/bookmarks").then(r => setData(r.data));

  useEffect(() => { if (user) refresh(); }, [user]);

  if (loading) return <div className="p-12 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;

  const confirmRemove = async () => {
    const { target_type, target_id, title } = pending;
    setPending(null);
    // Optimistic UI
    setData(d => target_type === "blog"
      ? { ...d, blogs: d.blogs.filter(b => b.blog_id !== target_id) }
      : { ...d, pathways: d.pathways.filter(p => p.pathway_id !== target_id) });
    try {
      await api.post("/bookmarks/toggle", { target_type, target_id });
      toast.success(`Removed “${title}”`);
    } catch {
      toast.error("Could not remove bookmark");
      refresh();
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-16">
      <div className="text-xs uppercase tracking-[0.24em] text-sky-700 mb-3">Saved</div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <PageSections pageKey="bookmarks_intro" defaults={defaultBookmarksIntroSections()}/>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer select-none mt-2 shrink-0">
          <input
            type="checkbox"
            data-testid="bookmarks-filter-unread"
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
            className="w-4 h-4 accent-sky-700"
          />
          Only show unread
        </label>
      </div>

      <section className="mt-12">
        <h2 className="font-heading text-xl text-slate-800 mb-4">Blogs</h2>
        {(() => {
          const visibleBlogs = unreadOnly ? data.blogs.filter(b => !isRead(b.blog_id)) : data.blogs;
          if (data.blogs.length === 0) {
            return <div className="border border-dashed border-slate-300 rounded-lg p-8 text-slate-500">No bookmarked blogs.</div>;
          }
          if (visibleBlogs.length === 0) {
            return <div className="border border-dashed border-slate-300 rounded-lg p-8 text-slate-500">All bookmarked blogs are read.</div>;
          }
          return (
            <ol data-testid="bookmarks-blogs" className="divide-y divide-slate-200 border-t border-b border-slate-200">
              {visibleBlogs.map(b => (
              <li key={b.blog_id} className="group flex items-start gap-3 py-4 px-2 -mx-2 rounded hover:bg-slate-50">
                <Link to={`/blogs/${b.slug}`} className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className={`font-heading text-lg font-semibold ${isRead(b.blog_id) ? "text-slate-500" : "text-slate-900"}`}>
                      {b.title}
                    </div>
                    {isRead(b.blog_id) && <ReadBadge read/>}
                  </div>
                  <div className="text-sm text-slate-600 line-clamp-2 mt-1">{b.excerpt}</div>
                </Link>
                <button
                  data-testid={`bookmark-remove-blog-${b.blog_id}`}
                  onClick={() => setPending({ target_type: "blog", target_id: b.blog_id, title: b.title })}
                  className="shrink-0 text-slate-400 hover:text-red-600 transition-colors inline-flex items-center gap-1 text-xs uppercase tracking-widest px-2 py-1"
                  title="Remove bookmark"
                  aria-label={`Remove bookmark: ${b.title}`}
                >
                  <BookmarkX className="w-4 h-4"/>
                  <span className="hidden sm:inline">Remove</span>
                </button>
              </li>
              ))}
            </ol>
          );
        })()}
      </section>

      <section className="mt-12">
        <h2 className="font-heading text-xl text-slate-800 mb-4">Pathways</h2>
        {(() => {
          const visiblePathways = unreadOnly
            ? data.pathways.filter(p => {
                const total = p.blog_ids?.length || 0;
                const readCount = (p.blog_ids || []).filter(id => isRead(id)).length;
                return total === 0 || readCount < total;
              })
            : data.pathways;
          if (data.pathways.length === 0) {
            return <div className="border border-dashed border-slate-300 rounded-lg p-8 text-slate-500">No bookmarked pathways.</div>;
          }
          if (visiblePathways.length === 0) {
            return <div className="border border-dashed border-slate-300 rounded-lg p-8 text-slate-500">All bookmarked pathways are complete.</div>;
          }
          return (
            <ol data-testid="bookmarks-pathways" className="divide-y divide-slate-200 border-t border-b border-slate-200">
              {visiblePathways.map(p => {
              const total = p.blog_ids?.length || 0;
              const readCount = (p.blog_ids || []).filter(id => isRead(id)).length;
              return (
                <li key={p.pathway_id} className="group flex items-start gap-3 py-4 px-2 -mx-2 rounded hover:bg-slate-50">
                  <Link to={`/pathways/${p.slug}`} className="flex-1 min-w-0">
                    <div className="font-heading text-lg font-semibold text-slate-900">{p.title}</div>
                    <div className="text-sm text-slate-600 line-clamp-2 mt-0.5">{p.description}</div>
                    {total > 0 && (
                      <PathwayProgress read={readCount} total={total} size="sm" className="mt-3 max-w-md"/>
                    )}
                  </Link>
                  <button
                    data-testid={`bookmark-remove-pathway-${p.pathway_id}`}
                    onClick={() => setPending({ target_type: "pathway", target_id: p.pathway_id, title: p.title })}
                    className="shrink-0 text-slate-400 hover:text-red-600 transition-colors inline-flex items-center gap-1 text-xs uppercase tracking-widest px-2 py-1"
                    title="Remove bookmark"
                    aria-label={`Remove bookmark: ${p.title}`}
                  >
                    <BookmarkX className="w-4 h-4"/>
                    <span className="hidden sm:inline">Remove</span>
                  </button>
                </li>
              );
            })}
            </ol>
          );
        })()}
      </section>

      <AlertDialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent data-testid="bookmark-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this bookmark?</AlertDialogTitle>
            <AlertDialogDescription>
              You’re about to remove <span className="font-medium text-slate-800 dark:text-slate-200">“{pending?.title}”</span> from your bookmarks. You can bookmark it again anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bookmark-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="bookmark-remove-confirm-btn"
              onClick={confirmRemove}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
